import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AuthDialog } from "@/components/AuthDialog";
import { Heart, Building2, LogOut, FileText, LayoutDashboard, Menu } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 h-16 md:h-20 bg-white/85 backdrop-blur-xl border-b border-[#E8E6DF]">
      <div className="h-full max-w-[1600px] mx-auto px-4 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
          <div className="h-9 w-9 rounded-xl bg-[#1A365D] overflow-hidden flex items-center justify-center">
            <img src="https://customer-assets.emergentagent.com/job_senior-bed-finder/artifacts/a5csklwc_CareBed%20Logo%20-%203.png" alt="CareBed" className="h-9 w-9 object-cover" />
          </div>
          <span className="font-heading text-xl font-semibold text-[#1C211F]">{settings?.brand_name || "CareBed"}</span>
        </Link>

        <nav className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="sm:hidden h-10 w-10 rounded-xl flex items-center justify-center text-[#2A312D] hover:bg-[#EFEEE8]" data-testid="mobile-menu-trigger" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/search")} data-testid="mobile-nav-search">Find Care</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/blog")} data-testid="mobile-nav-blog">Blog</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/about")} data-testid="mobile-nav-about">About</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/for-facilities")} data-testid="mobile-nav-for-facilities">For Facilities</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/claim")} data-testid="mobile-nav-claim">Claim your facility</DropdownMenuItem>
              {user && user.role !== "director" && user.role !== "admin" && (
                <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="mobile-nav-favorites">Saved facilities</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/search" className="hidden sm:inline-flex">
            <Button variant="ghost" className="text-[#2A312D] hover:bg-[#EFEEE8] rounded-xl" data-testid="nav-search">
              Find Care
            </Button>
          </Link>
          <Link to="/blog" className="hidden sm:inline-flex">
            <Button variant="ghost" className="text-[#2A312D] hover:bg-[#EFEEE8] rounded-xl" data-testid="nav-blog">
              Blog
            </Button>
          </Link>
          <Link to="/about" className="hidden sm:inline-flex">
            <Button variant="ghost" className="text-[#2A312D] hover:bg-[#EFEEE8] rounded-xl" data-testid="nav-about">
              About
            </Button>
          </Link>
          <Link to="/for-facilities" className="hidden sm:inline-flex">
            <Button variant="ghost" className="text-[#2A312D] hover:bg-[#EFEEE8] rounded-xl" data-testid="nav-for-facilities">
              For Facilities
            </Button>
          </Link>
          {!user && (
            <Link to="/claim" className="hidden sm:inline-flex">
              <Button variant="outline" className="border-[#1A365D] text-[#1A365D] hover:bg-[#E3F3ED] rounded-xl" data-testid="nav-claim">
                <Building2 className="h-4 w-4 mr-1.5" /> Claim your facility
              </Button>
            </Link>
          )}
          {user ? (
            <>
              {user.role !== "director" && user.role !== "admin" && (
                <Link to="/favorites">
                  <Button variant="ghost" className="rounded-xl hidden sm:inline-flex" data-testid="nav-favorites">
                    <Heart className="h-4 w-4 mr-1.5" /> Saved
                  </Button>
                </Link>
              )}
              {user.role === "director" && (
                <Link to="/director">
                  <Button variant="ghost" className="rounded-xl" data-testid="nav-director">
                    <Building2 className="h-4 w-4 mr-1.5" /> My Facilities
                  </Button>
                </Link>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="h-9 w-9 rounded-full bg-[#1A365D] text-white text-sm font-medium flex items-center justify-center">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5 text-sm">
                    <div className="font-medium text-[#1C211F] truncate">{user.name}</div>
                    <div className="text-xs text-[#5C6660] truncate">{user.email}</div>
                    <div className="text-xs mt-1 inline-block px-2 py-0.5 rounded-full bg-[#E3F3ED] text-[#1A365D] capitalize">{user.role}</div>
                  </div>
                  <DropdownMenuSeparator />
                  {user.role === "director" && (
                    <DropdownMenuItem onClick={() => navigate("/director")} data-testid="menu-director">
                      <Building2 className="h-4 w-4 mr-2" /> My Facilities
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <>
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                        <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/admin/blog")} data-testid="menu-blog-admin">
                        <FileText className="h-4 w-4 mr-2" /> Manage blog
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/favorites")}>
                    <Heart className="h-4 w-4 mr-2" /> Saved facilities
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button onClick={() => setAuthOpen(true)} data-testid="header-login-btn"
              className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl">
              Log in
            </Button>
          )}
        </nav>
      </div>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}
